<script context="module">
  function convertStringToObject(value, size) {
    const result = {};
    for (let i = 0; i < size; i++) {
      result[i] = value[i] || '';
    }
    return result;
  }

  function genInitValue(value, size) {
    if (value && value.length === size) {
      return value.split('');
    }
    return [...Array(size)].map(() => '');
  }

  function getPastedText(e) {
    const win = window;
    if (win.clipboardData && win.clipboardData.getData) {
      // IE
      return win.clipboardData.getData('Text') || '';
    }
    return e.clipboardData.getData('Text') || '';
  }
</script>

<script>
  import {onMount, createEventDispatcher} from 'svelte';
  import Loading from '@/icons/Loading.svelte';
  import Success from '@/icons/Success.svelte';
  import Error from '@/icons/Error.svelte';

  let className = '';
  export {className as class};
  export let size = 6;
  export let explain = '';
  export let value = '';
  export let disabled = false;
  export let status = '';
  export let focus = true;

  const dispatch = createEventDispatcher();

  $: isDisabled = status === 'loading' || disabled;

  let activeIndex = -1;
  let container = null;

  let digits = genInitValue(value, size);
  let ds = convertStringToObject('', size);

  $: digitsCount = digits.filter(Boolean).length;

  $: {
    if (status === 'success') {
      activeIndex = -1;
    }
    if (status === 'fail') {
      digits = genInitValue('', size);
      ds = convertStringToObject('', size);
      moveCaret(0);
    }
  }

  $: {
    if (digitsCount === size) {
      dispatch('change', {value: digits.join('')});
    }
    // 值发生变化就同步数据
    if (digitsCount !== null) {
      dispatch('codeChange', {value: digits.join('')});
    }
  }

  onMount(() => {
    focus && moveCaret(0);
  });

  function handleClick(e) {
    e.preventDefault();
    moveCaret(digitsCount);
  }

  function moveCaret(index) {
    if (!container) {
      return;
    }
    if (index < 0) {
      index = 0;
    }
    if (index >= size) {
      index = size - 1;
    }
    const inputs = container.querySelectorAll('input');
    inputs[index].focus();
  }

  function handleKeyDownFun(index) {
    return function (e) {
      const {keyCode} = e;
      if (keyCode === 9) {
        // Tab
        if (index < size - 1) {
          e.preventDefault();
          return;
        }
      }
      if (keyCode === 8) {
        // Backspace
        if (digits[index] === '' && index > 0) {
          digits[index - 1] = '';
          digits = [...digits];
          moveCaret(index - 1);
          return;
        }
        if (digits[index] !== '' && index === size - 1) {
          digits[index] = '';
          digits = [...digits];
          return;
        }
        digits[index] = '';
        digits = [...digits];
        moveCaret(index - 1);
      }
    };
  }

  function handleInputFun(index) {
    return function (e) {
      dispatch('input');
      const {value} = e.target;
      if (/^\d$/.test(value)) {
        digits[index] = value;
        digits = [...digits];
        moveCaret(index + 1);
      }
      ds[index] = '';
    };
  }

  function handlePasteInput(e) {
    e.preventDefault();
    const pastedText = getPastedText(e);
    if (pastedText.length < 1 || isNaN(pastedText)) {
      return;
    }
    const pastedDigits = pastedText.substr(0, size).split('');
    digits = digits.map((o, index) => pastedDigits[index] || '');
    const inputs = container.querySelectorAll('input');
    inputs[pastedDigits.length === size ? pastedDigits.length - 1 : pastedDigits.length].focus();
  }

  function handleFocusFun(index) {
    return function () {
      activeIndex = index;
    };
  }
</script>

<style>
  .wrap {
    -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
  }
  .wrap .disabled {
    pointer-events: none;
    opacity: 0.8;
  }
  .digits {
    display: flex;
    justify-content: space-between;
    align-items: center;
    user-select: none;
  }
  .digit-box {
    display: flex;
    justify-content: center;
    box-sizing: border-box;
    border-bottom: solid 2px #e5e5e5;
    width: 48px;
    height: 48px;
  }
  .active.digit-box {
    border-bottom-color: var(--blue-main);
  }
  .disabled.digits:hover .digit-box,
  .disabled .digit-box {
    border-bottom-color: #e5e5e5;
  }
  .fail .digit-box,
  .fail:hover .digit-box {
    border-bottom: solid 1px #f24e53;
  }
  .txt {
    display: inline-block;
    line-height: 48px;
    font-weight: var(--fw3);
    font-size: 16px;
    color: var(--T1);
    pointer-events: none;
  }
  input {
    border: none;
    border-radius: 0;
    width: 1px;
    height: 45px;
    text-align: center;
    background: transparent;
    outline: none;
    -moz-appearance: textfield;
  }
  input::-webkit-outer-spin-button,
  input::-webkit-inner-spin-button {
    margin: 0;
    -webkit-appearance: none;
  }
  .loading :global(svg) {
    animation: rotate 1s linear infinite;
  }
  .explain {
    margin: 6px 0 0 0;
    text-align: left;
    font-size: 12px;
    color: #dc4a45;
  }
  @keyframes rotate {
    from {
      transform: rotate(0);
    }
    to {
      transform: rotate(360deg);
    }
  }
  .explain {
      margin: 6px 0 0 0;
      text-align: left;
      font-size: 12px;
      color: #dc4a45;
  }
  .digit-box.fail {
    border-bottom: 1px solid #f24e53 !important;
  }
  @media screen and (max-width: 640px) {
    .digit-box {
      margin: 0 8px;
      width: 32px;
      height: 48px;
    }
  }
</style>

<div class="wrap {className}">
  <div class="digits" class:fail={status === 'fail'} class:disabled={isDisabled} bind:this={container} on:click={handleClick}>
    {#each digits as d, index}
      <div class="digit-box df aic jcc" class:active={activeIndex === index} class:fail={status === 'fail'}>
        <span class="txt">{d}</span>
        <input
          type="number"
          maxlength="1"
          max="9"
          bind:value={ds[index]}
          data-index={index}
          on:click={handleClick}
          on:keydown={handleKeyDownFun(index)}
          on:input={handleInputFun(index)}
          on:paste={handlePasteInput}
          on:focus={handleFocusFun(index)}
        />
      </div>
    {/each}
  </div>
  <div class="explain">{explain}</div>
</div>
